import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil as rxTakeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from '../../../Services/auth.service';
import { Global } from '../../../Shared/global';
import { ForgotPasswordResponse, ChangePasswordRequest } from '../../../Models/Common/ForgotPasswordModels';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppContext } from '../../../Shared/common';
import { AppConfigService } from '../../../Services/appconfig.service';
import { ApplicationConfiguration } from '../../../Models/ApplicationConfiguration';

@Component({
  selector: 'reset-password',
  templateUrl: './resetPasswordComponent.html',
  styleUrls: ['./resetPasswordComponent.css']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  forgotPasswordForm: FormGroup;
  isLoading = false;
  isGeneratingCode = false;
  isSubmitting = false;
  isVerificationCodeExpired = false;
  errorMessage = '';
  successMessage = '';
  showPassword = false;
  showConfirmPassword = false;
  resetFormEnabled = false;

  // Timer properties
  timerSeconds = 0;
  timerMinutes = 0;
  timerDisplay = '';
  private timerInterval: any;
  private readonly VERIFICATION_CODE_TIMEOUT_SECONDS = 300; // 5 minutes

  // Verification code response data
  private verificationCodeResponse: ForgotPasswordResponse | null = null;

  private destroy$ = new Subject<void>();
  private usernameCheckSubject$ = new Subject<string>();
  isCheckingUsername = false;
  usernameNotFound = false;
  userFound = false;


  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private appctx: AppContext,
    private appConfigService: AppConfigService,
    private sanitizer: DomSanitizer
  ) {
    this.forgotPasswordForm = this.createForm();
  }

  ngOnInit(): void {
    // Ensure user is logged in; this route is protected by AuthGuard
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
      return;
    }
    // Setup username validation
    this.setupUsernameValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopTimer();
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      userName: ['', [Validators.required, Validators.minLength(3)]],
      verificationCode: ['', [Validators.required, Validators.minLength(4)]],
      newPassword: ['', [Validators.required, Validators.minLength(6), this.passwordValidator]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]]
    }, { validators: this.passwordMatchValidator });
  }

  private passwordValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const password = control.value;
    const hasUppercase = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);

    if (!hasUppercase || !hasDigit) {
      return {
        passwordStrength: {
          requiresUppercase: !hasUppercase,
          requiresDigit: !hasDigit
        }
      };
    }

    return null;
  }

  private passwordMatchValidator(formGroup: AbstractControl): ValidationErrors | null {
    const newPassword = formGroup.get('newPassword');
    const confirmPassword = formGroup.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    if (newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      if (confirmPassword.errors?.['passwordMismatch']) {
        confirmPassword.setErrors(null);
      }
    }

    return null;
  }

  onGenerateVerificationCode(): void {
    const userNameControl = this.forgotPasswordForm.get('userName');

    if (!userNameControl || !userNameControl.value) {
      this.errorMessage = 'Please enter the username';
      return;
    }

    if (userNameControl.invalid) {
      userNameControl.markAsTouched();
      this.errorMessage = 'Username is invalid';
      return;
    }

    this.isGeneratingCode = true;
    this.errorMessage = '';
    this.successMessage = '';

    const requesterEmail = this.authService.getCurrentUser()?.Email || '';

    this.authService
      .requestForgotPasswordCodeByRequestor(userNameControl.value, requesterEmail)
      .pipe(rxTakeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isGeneratingCode = false;
          this.verificationCodeResponse = response;
          this.isVerificationCodeExpired = false;

          this.successMessage = 'Verification code sent. Check the user email.';
          Global.Log(`Admin requested password reset for user: ${userNameControl.value}`);

          this.startTimer();
          this.errorMessage = '';
          setTimeout(() => {
            const verificationCodeInput = document.getElementById('verificationCode') as HTMLInputElement;
            if (verificationCodeInput) {
              verificationCodeInput.focus();
            }
          }, 500);
        },
        error: (error) => {
          this.isGeneratingCode = false;
          this.verificationCodeResponse = null;

          if (Array.isArray(error.errors)) {
            this.errorMessage = error.errors.join('\n');
          } else if (error.message) {
            this.errorMessage = error.message;
          } else if (typeof error === 'string') {
            this.errorMessage = error;
          } else {
            this.errorMessage = 'Failed to request verification code. Please try again.';
          }

          Global.LogError('Verification code request failed:', error);
          this.stopTimer();
        }
      });
  }

  // Timer helpers (copied behavior)
  private startTimer(): void {
    this.stopTimer();
    this.timerSeconds = this.VERIFICATION_CODE_TIMEOUT_SECONDS;
    this.updateTimerDisplay();

    this.timerInterval = setInterval(() => {
      if (this.timerSeconds <= 0) {
        this.isVerificationCodeExpired = true;
        this.stopTimer();
        return;
      }
      this.timerSeconds--;
      this.updateTimerDisplay();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /**
   * Get form control getters
   */
  get userNameControl() {
    return this.forgotPasswordForm.get('userName');
  }

  get verificationCode() {
    return this.forgotPasswordForm.get('verificationCode');
  }

  get newPassword() {
    return this.forgotPasswordForm.get('newPassword');
  }

  get confirmPassword() {
    return this.forgotPasswordForm.get('confirmPassword');
  }

  /**
   * Triggered when username input loses focus
   */
  onUsernameBlur(): void {
    const username = this.forgotPasswordForm.get('userName')?.value;
    if (username && username.length >= 3) {
      this.checkUsername(username);
    } else {
      this.resetUserLookup();
    }
  }

  private resetUserLookup(): void {
    this.isCheckingUsername = false;
    this.usernameNotFound = false;
    this.userFound = false;
  }

  private checkUsername(username: string): void {
    this.isCheckingUsername = true;
    this.errorMessage = '';
    this.usernameNotFound = false;
    this.userFound = false;

    this.authService.getUserByUserName(username).pipe(rxTakeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        if (response && response.UserName && response.IsRegistered) {
          this.userFound = true;
          this.usernameNotFound = false;
        } else {
          this.userFound = false;
          this.usernameNotFound = true;
        }
        this.isCheckingUsername = false;
      },
      error: () => {
        this.isCheckingUsername = false;
        this.userFound = false;
        this.usernameNotFound = true;
      }
    });
  }

  /**
   * Username validation setup (debounced lookup)
   */
  private setupUsernameValidation(): void {
    // Subscribe to value changes on the userName control
    const ctrl = this.forgotPasswordForm.get('userName');
    if (!ctrl) return;

    ctrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), rxTakeUntil(this.destroy$))
      .subscribe((username: string) => {
        if (username && username.length >= 3) {
          this.checkUsername(username);
        } else {
          this.resetUserLookup();
        }
      });
  }

  private updateTimerDisplay(): void {
    this.timerMinutes = Math.floor(this.timerSeconds / 60);
    const secs = this.timerSeconds % 60;
    this.timerDisplay = `${this.timerMinutes}:${secs.toString().padStart(2, '0')}`;
  }
   

  isVerificationCodeAvailable(): boolean {
    return !!this.verificationCodeResponse;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Submit change password - reuse logic from forgot password component
  onSubmit(): void {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    if (!this.verificationCodeResponse) {
      this.errorMessage = 'Please generate verification code first';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const req: ChangePasswordRequest = {
      UserName: this.forgotPasswordForm.get('userName')?.value,
      ValidationCode: this.forgotPasswordForm.get('verificationCode')?.value,
      Password: this.forgotPasswordForm.get('newPassword')?.value,
      RequestId: this.verificationCodeResponse.RequestId,
      ExpiresAt: this.verificationCodeResponse.ExpiresAt
    };

    this.authService.changePassword(req).pipe(rxTakeUntil(this.destroy$)).subscribe({
      next: (resp) => {
        this.isSubmitting = false;
        if (resp && (resp.toString() === "Password reset successfully." || resp.success)) {
          this.successMessage = 'Password changed successfully';
          this.stopTimer();
          this.forgotPasswordForm.reset();
        } else {
          this.errorMessage = resp?.message || 'Password change failed';
        }
        this.resetFormEnabled = true;
      },
      error: (err) => {
        this.isSubmitting = false;
        if (Array.isArray(err.errors)) {
          this.errorMessage = err.errors.join('\n');
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'Password change failed. Please try again.';
        }
        this.resetFormEnabled = true;
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.forgotPasswordForm.get(controlName);
    if (!control) return 'Invalid input';

    if (control.hasError('required')) {
      return `${this.getFieldLabel(controlName)} is required`;
    }

    if (control.hasError('minlength')) {
      const minLength = control.getError('minlength').requiredLength;
      return `${this.getFieldLabel(controlName)} must be at least ${minLength} characters`;
    }

    if (control.hasError('passwordStrength')) {
      const errors = control.getError('passwordStrength');
      let message = 'Password must contain ';
      if (errors.requiresUppercase) {
        message += 'at least one uppercase letter';
      }
      if (errors.requiresDigit) {
        message += errors.requiresUppercase ? ' and one digit' : 'at least one digit';
      }
      return message;
    }

    if (control.hasError('passwordMismatch')) {
      return 'Passwords do not match';
    }

    return 'Invalid input';
  }

  private getFieldLabel(controlName: string): string {
    const labels: { [key: string]: string } = {
      userName: 'Username',
      verificationCode: 'Verification Code',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password'
    };
    return labels[controlName] || controlName;
  }

  resetForm(): void {
    this.forgotPasswordForm.reset();
    this.successMessage = '';
    this.errorMessage = '';
    this.verificationCodeResponse = null;
    this.resetFormEnabled = false;
    this.timerSeconds = 0;
    this.timerMinutes = 0;
    this.timerDisplay = '';
  }

}
